import { useQuantities, type Quantity } from "../../../../api/useQuantities";
import { useSetups } from "../../../../api/useSetups";
import { QuantityColumnList } from "../schema/resource/columns";
import { FaultColumnsList } from "../schema/resource/columns";
import { useMemo } from "react";
import { uniqBy } from "diagnostics-lib/array";
import { useManualFailures } from "../../../../api/useManualFailures";
import { indexBy } from "diagnostics-lib/iterable";
export const AGGREGATE_UNEXPLAINED_LOSSES = "Unexplained Losses";
export const AGGREGATE_NO_SYSTEM = "No system";

const AGGREGATE_IDENTIFIERS = [{ name: AGGREGATE_UNEXPLAINED_LOSSES, unit: null }];

export const useAvailableResources = ({ projectId }: { projectId: string }) => {
  const quantities = useQuantities(projectId);
  const setups = useSetups(projectId);
  const manualFailures = useManualFailures(projectId);

  const allDiagnosisInputs = useMemo(
    () => setups.flatMap(setup => setup.diagnosisInputs),
    [setups],
  );
  const diagnosisInputsByQuantityId = useMemo(
    () => indexBy(allDiagnosisInputs, input => input.quantityId),
    [allDiagnosisInputs],
  );

  const quantityIdentifiers = useMemo(
    () =>
      quantities
        .map(quantity => ({
          id: quantity.id,
          name: quantity.name,
          unit: quantity.unit,
          isFormula: quantity.type === "FORMULA",
          usedForExternalGaps: setups.some(setup =>
            setup.simulationInputs.find(
              input =>
                input.quantityId === quantity.id && typeof input.orderExternalGap === "number",
            ),
          ),
          usedForSimulationOutputs: setups.every(setup =>
            setup.simulationOutputs.some(output => output.quantityId === quantity.id),
          ),
          usedForSimulationInputs: setups.some(setup =>
            setup.simulationInputs.some(input => input.quantityId === quantity.id),
          ),
          isActivePower: quantity.qoiOrder !== null && quantity.qoiRole === "POWER",
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [quantities, setups],
  );

  const faultIdentifiers = useMemo(() => {
    const setupFailures = setups
      .flatMap(setup => setup.failures)
      .map(f => ({ ...f }))
      .sort((a, b) => a.name.localeCompare(b.name));

    const manualFailuresTagged = manualFailures
      .map(f => ({ ...f }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return uniqBy([...setupFailures, ...manualFailuresTagged], item => item.name).map(fault => ({
      name: fault.name,
      displayName: "amplitude" in fault ? fault.displayName : null,
      unit: "unit" in fault ? fault.unit : null,
      // WARNING: As the incoming fault hasn't been mapped to the front-end system of No system we
      // still have to use null here
      system: fault.systemId === null ? AGGREGATE_NO_SYSTEM : fault.systemId,
      manual: !("amplitude" in fault),
    }));
  }, [manualFailures, setups]);

  const performanceIndicators = useMemo(
    () =>
      quantities
        .filter((q): q is Quantity & { qoiOrder: number } => typeof q.qoiOrder === "number")
        .sort((a, b) => a.qoiOrder - b.qoiOrder)
        .map(q => ({
          name: q.name,
          unit: q.unit,
          order: q.qoiOrder,
          isActivePower: q.qoiOrder !== null && q.qoiRole === "POWER",
        })),
    [quantities],
  );

  const observables = useMemo(
    () =>
      quantities
        .filter(q =>
          setups.some(setup => setup.diagnosisInputs.find(input => input.quantityId === q.id)),
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(q => ({
          name: q.name,
          unit: q.unit,
          order: q.qoiOrder,
          enabled: diagnosisInputsByQuantityId.get(q.id)?.enabled ?? true,
        })),
    [diagnosisInputsByQuantityId, quantities, setups],
  );

  const systemIdentifiers = useMemo(
    () =>
      uniqBy(
        faultIdentifiers.map(f => ({ name: f.system, unit: null })),
        item => item.name,
      ),
    [faultIdentifiers],
  );

  return useMemo(
    () => ({
      quantity: {
        kind: "QUANTITY" as const,
        identifiers: quantityIdentifiers,
        columns: QuantityColumnList,
      },
      fault: { kind: "FAULT" as const, identifiers: faultIdentifiers, columns: FaultColumnsList },
      system: { kind: "SYSTEM" as const, identifiers: systemIdentifiers, columns: [] },
      aggregate: { kind: "AGGREGATE" as const, identifiers: AGGREGATE_IDENTIFIERS, columns: [] },
      performanceIndicators,
      observables,
    }),
    [quantityIdentifiers, faultIdentifiers, systemIdentifiers, performanceIndicators, observables],
  );
};
